import { ContactAppUser, Contact, Tag } from "./contacts";
import { Company } from "./company";
import { MutableRefObject } from "react";

export enum DealType {
  NEW_BUSINESS = "NEW_BUSINESS",
  RENEWAL = "RENEWAL",
  UPSELL = "UPSELL",
  CROSS_SELL = "CROSS_SELL",
  OTHER = "OTHER",
}

export enum DealStage {
  APPOINTMENT_SCHEDULED = "APPOINTMENT_SCHEDULED",
  QUALIFIED_TO_BUY = "QUALIFIED_TO_BUY",
  PRESENTATION_SCHEDULED = "PRESENTATION_SCHEDULED",
  DECISION_MAKER_BOUGHT_IN = "DECISION_MAKER_BOUGHT_IN",
  CONTRACT_SENT = "CONTRACT_SENT",
  CLOSED_WON = "CLOSED_WON",
  CLOSED_LOST = "CLOSED_LOST",
}

export enum DealPriority {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
  URGENT = "URGENT",
}

export enum Pipeline {
  SALES = "SALES",
  MARKETING = "MARKETING",
}

export const DEAL_TYPE_LABELS: Record<DealType, string> = {
  [DealType.NEW_BUSINESS]: "New Business",
  [DealType.RENEWAL]: "Renewal",
  [DealType.UPSELL]: "Upsell",
  [DealType.CROSS_SELL]: "Cross Sell",
  [DealType.OTHER]: "Other",
};

export const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  [DealStage.APPOINTMENT_SCHEDULED]: "Appointment Scheduled",
  [DealStage.QUALIFIED_TO_BUY]: "Qualified To Buy",
  [DealStage.PRESENTATION_SCHEDULED]: "Presentation Scheduled",
  [DealStage.DECISION_MAKER_BOUGHT_IN]: "Decision Maker Bought-In",
  [DealStage.CONTRACT_SENT]: "Contract Sent",
  [DealStage.CLOSED_WON]: "Closed Won",
  [DealStage.CLOSED_LOST]: "Closed Lost",
};

export const DEAL_PRIORITY_LABELS: Record<DealPriority, string> = {
  [DealPriority.LOW]: "Low",
  [DealPriority.MEDIUM]: "Medium",
  [DealPriority.HIGH]: "High",
  [DealPriority.URGENT]: "Urgent",
};

export const PIPELINE_LABELS: Record<Pipeline, string> = {
  [Pipeline.SALES]: "Sales",
  [Pipeline.MARKETING]: "Marketing",
};

export const DEFAULT_PIPELINE = Pipeline.SALES;

export const PIPELINE_OPTIONS = Object.values(Pipeline).map((p) => ({
  value: p,
  label: PIPELINE_LABELS[p],
}));

export const DEAL_STAGE_OPTIONS = Object.values(DealStage).map((s) => ({
  value: s,
  label: DEAL_STAGE_LABELS[s],
}));

export const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP', 'AED'].map((c) => ({
  value: c,
  label: c,
}));

export const DEAL_TYPE_OPTIONS = Object.values(DealType).map((t) => ({
  value: t,
  label: DEAL_TYPE_LABELS[t],
}));

export const PRIORITY_OPTIONS = Object.values(DealPriority).map((p) => ({
  value: p,
  label: DEAL_PRIORITY_LABELS[p],
}));

export const PRIORITY_PILL: Record<DealPriority, string> = {
  [DealPriority.LOW]: 'bg-[#22C55E]/15 text-[#22C55E]',
  [DealPriority.MEDIUM]: 'bg-[#FBBF24]/15 text-[#FBBF24]',
  [DealPriority.HIGH]: 'bg-[#F97316]/15 text-[#F97316]',
  [DealPriority.URGENT]: 'bg-[#EF4444]/15 text-[#EF4444]',
};


export const DEAL_STAGE_TEXT: Record<DealStage, string> = {
  [DealStage.APPOINTMENT_SCHEDULED]: 'text-[#7db4ff]',
  [DealStage.QUALIFIED_TO_BUY]: 'text-[#06b6d4]',
  [DealStage.PRESENTATION_SCHEDULED]: 'text-emphasis',
  [DealStage.DECISION_MAKER_BOUGHT_IN]: 'text-[#b07fc7]',
  [DealStage.CONTRACT_SENT]: 'text-[#f97316]',
  [DealStage.CLOSED_WON]: 'text-[#10b981]',
  [DealStage.CLOSED_LOST]: 'text-[#ef4444]',
};

/** Board column accent — the top bar and header dot. Same hues as DEAL_STAGE_TEXT. */
export const DEAL_STAGE_ACCENT: Record<DealStage, string> = {
  [DealStage.APPOINTMENT_SCHEDULED]: 'bg-[#7db4ff]',
  [DealStage.QUALIFIED_TO_BUY]: 'bg-[#06b6d4]',
  [DealStage.PRESENTATION_SCHEDULED]: 'bg-[#8b5cf6]',
  [DealStage.DECISION_MAKER_BOUGHT_IN]: 'bg-[#b07fc7]',
  [DealStage.CONTRACT_SENT]: 'bg-[#f97316]',
  [DealStage.CLOSED_WON]: 'bg-[#10b981]',
  [DealStage.CLOSED_LOST]: 'bg-[#ef4444]',
};

/** Accents for stages an admin added, handed out by lane position. */
const CUSTOM_STAGE_ACCENTS = [
  'bg-[#7db4ff]',
  'bg-[#06b6d4]',
  'bg-[#8b5cf6]',
  'bg-[#b07fc7]',
  'bg-[#f97316]',
  'bg-[#fbbf24]',
  'bg-[#ec4899]',
];

/**
 * A board lane's accent. Closed lanes are always green / red; the built-in
 * stages keep their colour; anything else cycles through the custom palette.
 */
export const getDealStageAccent = (
  column: Pick<DealKanbanColumn, 'stageKey' | 'closedWon' | 'closedLost'>,
  index: number,
) => {
  if (column.closedWon) return 'bg-[#10b981]';
  if (column.closedLost) return 'bg-[#ef4444]';
  return (
    DEAL_STAGE_ACCENT[column.stageKey as DealStage] ??
    CUSTOM_STAGE_ACCENTS[index % CUSTOM_STAGE_ACCENTS.length]
  );
};

export const DEAL_TYPE_PILL: Record<DealType, string> = {
  [DealType.NEW_BUSINESS]: 'bg-[#7db4ff]/15 text-[#7db4ff]',
  [DealType.RENEWAL]: 'bg-[#10b981]/15 text-[#10b981]',
  [DealType.UPSELL]: 'bg-[#8b5cf6]/15 text-[#8b5cf6]',
  [DealType.CROSS_SELL]: 'bg-[#06b6d4]/15 text-[#06b6d4]',
  [DealType.OTHER]: 'bg-[#9ca3b0]/15 text-neutral-faded',
};

export const DEAL_PRIORITY_TEXT: Record<DealPriority, string> = {
  [DealPriority.LOW]: 'text-[#22C55E]',
  [DealPriority.MEDIUM]: 'text-[#FBBF24]',
  // URGENT takes the PMO red; HIGH steps down to orange so the two never blur.
  [DealPriority.HIGH]: 'text-[#F97316]',
  [DealPriority.URGENT]: 'text-[#EF4444]',
};

export const dealPageInitialValues: DealFormValues = {
  name: '',
  // Left empty on purpose: the user picks a pipeline themselves rather than
  // inheriting Sales without noticing. `Pipeline is required` enforces it.
  pipeline: '',
  dealStage: '',
  currency: 'INR',
  amount: '',
  closeDate: '',
  owner: '',
  dealType: '',
  priority: '',
  description: '',
};

/** Fields the preview checklist tracks — the three the API treats as mandatory. */
export const DEAL_PAGE_REQUIRED_FIELDS: { key: 'name' | 'pipeline' | 'dealStage' | "amount" ; label: string }[] = [
  { key: 'name', label: 'Deal name' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'dealStage', label: 'Deal stage' },
  { key: 'amount', label: 'Amount' },
  // { key: 'associations', label: 'Associations' },

];

export const DESCRIPTION_MAX = 500;

export interface CreateDealPayload {
  dealName: string;
  pipeline?: string;
  /** A CRM value key — the built-in stages or any an admin adds in Settings → CRM Values. */
  dealStage?: string;
  amount?: number;
  currency?: string;
  closeDate?: string;
  dealOwnerUuid?: string;
  dealType?: DealType;
  priority?: DealPriority;
  associatedContactUuid?: string;
  associatedCompanyUuid?: string;
  associatedContactReferences?: string[];
  associatedCompanyReferences?: string[];
  associationLabel?: string;
  lineItemsJson?: string;
  description?: string;
  timezone?: string;
  tags?: string[];
}


export interface UpdateDealPayload {
  dealUuid: string;
  dealName?: string;
  pipeline?: string;
  /** A CRM value key — the built-in stages or any an admin adds in Settings → CRM Values. */
  dealStage?: string;
  amount?: number;
  currency?: string;
  closeDate?: string;
  dealOwnerUuid?: string;
  dealType?: DealType;
  priority?: DealPriority;
  description?: string;
}


export interface UpdateDealKanbanPositionPayload {
  dealUuid: string;
  /** The `stageKey` of the lane the card was dropped in. */
  targetStage: string;
  previousDealUuid: string | null;
  nextDealUuid: string | null;
  timezone?: string;
}

export interface DealKanbanPosition {
  dealUuid: string;
  dealStage: string;
  dealStageLabel?: string;
  kanbanOrder: number;
  stageChangedAt: string;
}

/**
 * One lane of GET /crm/deals/kanban. Lanes come from CRM Values → Deal Stage,
 * already in board order, so the board renders them as they arrive.
 */
export interface DealKanbanColumn {
  stageUuid: string;
  /** CRM Value key — dynamic, admins can add more. Never hardcode. */
  stageKey: string;
  stageName: string;
  /**
   * INACTIVE lanes are only returned while they still hold deals. Cards may leave
   * or be reordered within it, but none may enter.
   */
  status: "ACTIVE" | "INACTIVE";
  stageOrder: number;
  probability?: number;
  closedWon: boolean;
  closedLost: boolean;
  /** Whole column, not just `deals`. */
  dealCount: number;
  /** Whole column, in USD. */
  totalAmount: number;
  hasMore: boolean;
  /** Page 0 of the lane — the same list `GET /crm/deals?dealStage=…&page=0` returns. */
  deals: Deal[];
}

export interface GetDealKanbanParams {
  /** Deals per column. */
  size?: number;
}

export interface DealAssociations {
  contactUuids: string[];
  companyUuids: string[];
}

/**
 * Which entity a deal is being linked to. One discriminated union instead of a
 * bag of optional uuids, so "both set" / "neither set" can't be constructed.
 */
export type DealLinkScope =
  | { type: "contact"; uuid: string }
  | { type: "company"; uuid: string };

export interface Deal {
  uuid: string;
  name: string;
  organisationUuid: string;
  organisationName: string;
  pipeline: Pipeline;
  pipelineLabel: string;
  /** A CRM value key — the built-in stages or any an admin adds in Settings → CRM Values. */
  dealStage: string;
  dealStageLabel: string;
  dealStageDescription?: string;
  dealStageProbability?: number;
  closedWon: boolean;
  closedLost: boolean;
  amount: number;
  currency: string;
  closeDate: string;
  /** Creator of the deal; `owner` is who it is assigned to. */
  appUser?: ContactAppUser;
  owner?: ContactAppUser;
  /** Server-computed edit access. `false` → view-only: no drag on the board. */
  canEdit?: boolean | null;
  dealType?: DealType;
  dealKey?: string;
  priority?: DealPriority;
  associatedContactUuid?: string;
  associatedCompanyUuid?: string;
  associationLabel?: string;
  lineItemsJson?: string;
  description?: string;
  tags?: Tag[];
  createdAt: string;
  lastModifiedAt?: string;
  lastActivityAt: string;
  stageChangedAt?: string;
  overdueTasks:number
  dealValue:number
  /** `amount` converted to USD by the backend (latest stored rate). Absent/null if it can't be converted. */
  usdAmount?: number | null
  activitiesCount:number
  notesCount:number
  tasksCount:number
}

/** GET /crm/deals */
export interface GetDealsParams {
  search?: string;
  /** A stage key — the List view's built-ins or a board lane's `stageKey`. */
  dealStage?: string;
  page?: number;
  size?: number;
}


export interface DealAssociatedContact
  extends Pick<
    Contact,
    | "uuid"
    | "contactKey"
    | "firstName"
    | "lastName"
    | "fullName"
    | "email"
    | "phone"
    | "jobTitle"
    | "lifecycleStage"
    | "engagementStatus"
    | "appUser"
    | "profileImageUrl"
  > {
  associationLabel?: string;
  primaryContact?: boolean;
  associatedAt?: string;
}

export interface DealAssociatedCompany
  extends Pick<
    Company,
    | "uuid"
    | "companyKey"
    | "name"
    | "industry"
    | "cityLocation"
    | "websiteUrl"
    | "phone"
    | "linkedInUrl"
    | "appUser"
  > {
  slug?: string;
  primaryCompany?: boolean;
  associatedAt?: string;
}

export interface DealDetail extends Deal {
  associatedContacts?: DealAssociatedContact[];
  associatedCompanies?: DealAssociatedCompany[];
}

export interface DealOption {
  uuid: string;
  dealKey?: string;
  name: string;
  pipeline?: string;
  pipelineLabel?: string;
  dealStage?: DealStage;
  dealStageLabel?: string;
  amount?: number;
  currency?: string;
  closeDate?: string;
  dealType?: DealType;
  priority?: DealPriority;
  appUser?: ContactAppUser;
  associated: boolean;
}

export interface GetDealOptionsParams {
  search?: string;
  page?: number;
  limit?: number;
}

export interface DealOptionsPage {
  content: DealOption[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface DealFormValues {
  name: string;
  pipeline: string;
  /** A CRM value key — the built-in stages or any an admin adds in Settings → CRM Values. */
  dealStage: string;
  currency: string;
  amount: string;
  closeDate: string;
  owner: string;
  dealType: DealType | '';
  priority: DealPriority | '';
  description: string;
}

export interface NonAssociatedDealParams {
  dealUuid: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UpdateDealAssociationsPayload {
  dealUuid: string;
  contactReferences?: string[];
  companyReferences?: string[];
}

/** GET /crm/deals/key/{dealKey}/uuid */
export interface DealUuidResolution {
  uuid: string;
  dealKey: string;
}


export type DealsView = "list" | "board";

export const VIEW_TABS: { id: DealsView; icon: string; label: string }[] = [
  { id: "list", icon: "/assets/list.png", label: "List" },
  { id: "board", icon: "/assets/board.png", label: "Board" },
];


export interface ColumnDropGeometry {
  rows: { top: number; height: number }[];
  gapHeight: number;
}

export type ColumnGeometryRegistry = MutableRefObject<
  Map<string, () => ColumnDropGeometry | null>
>;

export interface DealStageColumnProps {
  /** Lane metadata and page 0 from GET /crm/deals/kanban. */
  column: DealKanbanColumn;
  accent: string;
  deals: Deal[];
  search?: string;
  onDealsFetched: (stage: string, deals: Deal[], generation: number) => void;
  fetchGeneration: number;
  onDealClick?: (deal: Deal) => void;
  onAddDeal?: (stage: string) => void;
  dropIndex?: number | null;
  draggedCardHeight?: number;
  activeDealUuid?: string;
  geometryRegistry?: ColumnGeometryRegistry;
}

export const COLUMN_PAGE_SIZE = 20;

export const CARD_GAP = 12;

export const DEFAULT_CARD_HEIGHT = 130;

export const ESTIMATED_ROW_HEIGHT = DEFAULT_CARD_HEIGHT + CARD_GAP;